import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import LangLink from '../components/LangLink'
import Section from '../components/Section'

/* ---- minimal industrial line icons (stroke, currentColor, no fill) ---- */
function IconSourcing() {
  return (
    <svg viewBox="0 0 32 32" width="32" height="32" fill="none" aria-hidden="true">
      <path
        d="M16 3 4 9v14l12 6 12-6V9L16 3Z"
        stroke="currentColor"
        strokeWidth="1.4"
      />
      <path d="M4 9l12 6 12-6M16 15v14" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  )
}
function IconTrust() {
  return (
    <svg viewBox="0 0 32 32" width="32" height="32" fill="none" aria-hidden="true">
      <path
        d="M16 3 5 7v8c0 7 4.7 11.5 11 14 6.3-2.5 11-7 11-14V7L16 3Z"
        stroke="currentColor"
        strokeWidth="1.4"
      />
      <path d="m11 16 3.5 3.5L22 12" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  )
}
function IconLogistics() {
  return (
    <svg viewBox="0 0 32 32" width="32" height="32" fill="none" aria-hidden="true">
      <circle cx="16" cy="16" r="13" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M3 16h26M16 3c3.5 3.6 5.4 8.1 5.4 13S19.5 25.4 16 29c-3.5-3.6-5.4-8.1-5.4-13S12.5 6.6 16 3Z"
        stroke="currentColor"
        strokeWidth="1.4"
      />
    </svg>
  )
}

const HERO_CLIPS = [
  { key: 'pellets', src: '/hero/hero-1-pellets.mp4' },
  { key: 'truck', src: '/hero/hero-2-truck.mp4' },
  { key: 'port', src: '/hero/hero-3-port.mp4' },
]

interface Stat {
  value: string
  label: string
}

function HomePage() {
  const { t } = useTranslation()

  // Hero: video slider only on a wide viewport with motion allowed. Mobile
  // and prefers-reduced-motion fall back to the poster still (no video bytes,
  // no animation). See the notes in index.css.
  const [heroVideo, setHeroVideo] = useState(false)
  const [activeClip, setActiveClip] = useState(0)

  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)')
    const wide = window.matchMedia('(min-width: 768px)')
    const update = () => setHeroVideo(!reduce.matches && wide.matches)
    update()
    reduce.addEventListener('change', update)
    wide.addEventListener('change', update)
    return () => {
      reduce.removeEventListener('change', update)
      wide.removeEventListener('change', update)
    }
  }, [])

  useEffect(() => {
    if (!heroVideo) return
    const id = window.setInterval(
      () => setActiveClip((i) => (i + 1) % HERO_CLIPS.length),
      6000,
    )
    return () => window.clearInterval(id)
  }, [heroVideo])

  // Subtle on-scroll reveal for the sections. Skipped entirely under
  // prefers-reduced-motion, and the hidden state is gated behind the
  // .home-reveal-ready class so a no-JS render shows everything.
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const els = document.querySelectorAll('.home .section')
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            e.target.classList.add('is-revealed')
            io.unobserve(e.target)
          }
        }
      },
      { rootMargin: '0px 0px -10% 0px' },
    )
    els.forEach((el) => io.observe(el))
    document.documentElement.classList.add('home-reveal-ready')
    return () => {
      io.disconnect()
      document.documentElement.classList.remove('home-reveal-ready')
    }
  }, [])

  const partnerStats = t('partnerChina.stats', {
    returnObjects: true,
  }) as Stat[]
  // founded, 2025 revenue, 2025 volume, storage capacity
  const heroStats = [
    partnerStats[0],
    partnerStats[5],
    partnerStats[4],
    partnerStats[2],
  ].filter(Boolean)

  return (
    <div className="home">
      {/* ---------------- Industrial hero ---------------- */}
      <section className="home-hero">
        <div className="home-hero-media" aria-hidden="true">
          {heroVideo ? (
            HERO_CLIPS.map((clip, i) => (
              <video
                key={clip.key}
                // React does not always reflect the muted prop to the DOM
                // attribute; without a genuinely-muted element the browser
                // blocks autoplay. Set it on the element directly.
                ref={(el) => {
                  if (el) {
                    el.muted = true
                    el.defaultMuted = true
                  }
                }}
                className={`home-hero-layer${i === activeClip ? ' is-active' : ''}`}
                src={clip.src}
                poster="/hero/hero-poster.jpg"
                muted
                loop
                autoPlay
                playsInline
                preload={i === 0 ? 'auto' : 'metadata'}
              />
            ))
          ) : (
            <picture>
              <source srcSet="/hero/hero-poster.webp" type="image/webp" />
              <img
                className="home-hero-layer is-active"
                src="/hero/hero-poster.jpg"
                alt=""
                width={1920}
                height={1080}
              />
            </picture>
          )}
          <div className="home-hero-scrim" />
        </div>

        <div className="home-hero-inner">
          <h1 className="display home-hero-title">
            {t('home.hero.headingA')}
            <br />
            {t('home.hero.headingB')}
          </h1>
          <p className="home-hero-support">{t('home.hero.support')}</p>
          <div className="home-hero-actions">
            <LangLink to="/how-to-order" className="btn btn--primary">
              {t('home.cta')}
            </LangLink>
            <LangLink to="/products" className="btn btn--secondary-on-dark">
              {t('home.hero.ctaSecondary')}
            </LangLink>
          </div>
        </div>
      </section>

      {/* ---------------- Core value proposition ---------------- */}
      <Section background="surface" space="md" className="home-values">
        <h2 className="eyebrow">{t('home.values.eyebrow')}</h2>
        <div className="home-values-grid">
          <article>
            <span className="home-value-icon">
              <IconSourcing />
            </span>
            <h3>{t('home.values.sourcingTitle')}</h3>
            <p>{t('home.values.sourcingBody')}</p>
          </article>
          <article>
            <span className="home-value-icon">
              <IconTrust />
            </span>
            <h3>{t('home.values.trustTitle')}</h3>
            <p>{t('home.values.trustBody')}</p>
          </article>
          <article>
            <span className="home-value-icon">
              <IconLogistics />
            </span>
            <h3>{t('home.values.logisticsTitle')}</h3>
            <p>{t('home.values.logisticsBody')}</p>
          </article>
        </div>
      </Section>

      {/* ---------------- Introducing Intraveo ---------------- */}
      <Section background="beige" space="lg" className="home-intro">
        <div className="home-split">
          <div className="home-split-text">
            <p className="eyebrow">{t('home.intro.eyebrow')}</p>
            <h2>{t('home.intro.heading')}</h2>
            <p>{t('home.intro.body')}</p>
            <dl className="home-facts">
              <div>
                <dt>{t('home.intro.fact1')}</dt>
                <dd>{t('home.intro.fact1Label')}</dd>
              </div>
              <div>
                <dt>{t('home.intro.fact2')}</dt>
                <dd>{t('home.intro.fact2Label')}</dd>
              </div>
            </dl>
            <LangLink to="/about" className="home-arrow-link">
              {t('home.intro.link')}
            </LangLink>
          </div>
          <picture>
            <source srcSet="/partner/warehouse.webp" type="image/webp" />
            <img
              src="/partner/warehouse.jpg"
              alt={t('home.intro.imgAlt')}
              className="home-split-img"
              loading="lazy"
              width={1042}
              height={638}
            />
          </picture>
        </div>
      </Section>

      {/* ---------------- Products teaser ---------------- */}
      <Section background="surface" space="lg" className="home-products">
        <p className="eyebrow">{t('home.products.eyebrow')}</p>
        <h2>{t('home.products.heading')}</h2>
        <p className="home-lead-narrow">{t('home.products.body')}</p>
        <div className="home-products-grid">
          <article className="home-product">
            <h3>{t('home.products.ppTitle')}</h3>
            <p>{t('home.products.ppBody')}</p>
          </article>
          <article className="home-product">
            <h3>{t('home.products.peTitle')}</h3>
            <p>{t('home.products.peBody')}</p>
          </article>
        </div>
        <p className="home-price-note">{t('products.priceValue')}</p>
        <LangLink to="/products" className="home-arrow-link">
          {t('home.products.link')}
        </LangLink>
      </Section>

      {/* ---------------- Chinese partner + scale (dark band) ----------------
          Real photos supplied by the partner (CCLIMC): facility.jpg as the
          darkened band background, tanks.jpg and gate.jpg beside the text. */}
      <Section background="dark" space="lg" className="home-partner">
        <div className="home-partner-inner">
          <div className="home-partner-cols">
            <div className="home-partner-text">
              <p className="eyebrow">{t('home.partner.eyebrow')}</p>
              <h2>{t('home.partner.heading')}</h2>
              <p>{t('home.partner.body')}</p>
              <p className="home-partner-note">{t('home.partner.note')}</p>
            </div>
            <div className="home-partner-figs">
              <picture>
                <source srcSet="/partner/tanks.webp" type="image/webp" />
                <img
                  src="/partner/tanks.jpg"
                  alt={t('home.partner.imgAlt')}
                  loading="lazy"
                  width={1163}
                  height={584}
                />
              </picture>
              <picture>
                <source srcSet="/partner/gate.webp" type="image/webp" />
                <img
                  src="/partner/gate.jpg"
                  alt={t('home.partner.imgAlt2')}
                  loading="lazy"
                  width={1279}
                  height={462}
                />
              </picture>
            </div>
          </div>
          <dl className="home-stats">
            {heroStats.map((s) => (
              <div key={s.label}>
                <dt>{s.value}</dt>
                <dd>{s.label}</dd>
              </div>
            ))}
          </dl>
          <LangLink
            to="/partners/china"
            className="home-arrow-link home-arrow-link--on-dark"
          >
            {t('home.partner.link')}
          </LangLink>
        </div>
      </Section>

      {/* ---------------- Why Intraveo ---------------- */}
      <Section background="beige" space="lg" className="home-why">
        <p className="eyebrow">{t('home.why.eyebrow')}</p>
        <h2>{t('home.why.heading')}</h2>
        <ul className="home-why-list">
          {[1, 2, 3, 4, 5, 6].map((n) => (
            <li key={n}>{t(`home.why.item${n}`)}</li>
          ))}
        </ul>
      </Section>

      {/* ---------------- How it works + logistics teaser ---------------- */}
      <Section background="surface" space="lg" className="home-how">
        <p className="eyebrow">{t('home.how.eyebrow')}</p>
        <h2>{t('home.how.heading')}</h2>
        <ol className="home-timeline">
          {[1, 2, 3, 4].map((n) => (
            <li key={n}>
              <span className="home-timeline-num">{n}</span>
              <h3>{t(`home.how.step${n}Title`)}</h3>
              <p>{t(`home.how.step${n}Body`)}</p>
            </li>
          ))}
        </ol>
        <p className="home-how-incoterms">{t('home.how.incoterms')}</p>
        <p className="home-how-links">
          <LangLink to="/how-to-order" className="home-arrow-link">
            {t('home.how.linkHow')}
          </LangLink>
          <LangLink to="/incoterms-logistics" className="home-arrow-link">
            {t('home.how.linkLogistics')}
          </LangLink>
        </p>
      </Section>

      {/* ---------------- Request a quote CTA band ---------------- */}
      <Section background="bordeaux" space="md" className="home-cta">
        <h2>{t('home.quote.heading')}</h2>
        <p>{t('home.quote.body')}</p>
        <LangLink to="/how-to-order" className="btn btn--on-dark">
          {t('home.cta')}
        </LangLink>
      </Section>

      {/* ---------------- Contact teaser ---------------- */}
      <Section background="beige" space="md" className="home-contact">
        <p className="eyebrow">{t('home.contact.eyebrow')}</p>
        <h2>{t('home.contact.heading')}</h2>
        <p>{t('home.contact.body')}</p>
        <p className="home-contact-address">
          {t('contact.details.addressValue')}
        </p>
        <LangLink to="/contact" className="btn btn--secondary">
          {t('home.contact.cta')}
        </LangLink>
      </Section>
    </div>
  )
}

export default HomePage
